/**
 * @fileoverview Loans & Advances — Product-wise Loan Portfolio Composition page.
 * @description Ported from the InsightBox Banking design system's own
 * `LoansAdvances/ProductWise.tsx`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  formatCr,
  loanProductMix,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import PieBreakdownCard from '@/shared/components/molecules/PieBreakdownCard'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { PieSegment } from '@/shared/components/molecules/PieBreakdownCard'

/**
 * @component
 */
function BankingLoansProductWisePage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.loansProductWise.kicker')}
      title={t('banking.loansProductWise.title')}
      description={t('banking.loansProductWise.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const mix = loanProductMix(data, fy, branchId)
  const total = mix.reduce((sum, m) => sum + m.amountCr, 0)
  // A multi-hue categorical palette, not a single-color ramp — every product gets its own distinct
  // color at similar visual weight, since the wedge size (not shade darkness) already carries the
  // magnitude.
  const colors = categoricalPalette(mix.length)
  const top2Share = mix[0]!.sharePct + mix[1]!.sharePct

  const segments: PieSegment[] = mix.map((m, i) => ({
    key: m.key,
    label: m.label,
    value: m.amountCr,
    pct: m.sharePct,
    color: colors[i]!,
  }))

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.loansProductWise.kpi.totalOutstanding'),
      value: formatCr(total),
      sublabel: t('banking.loansProductWise.kpi.acrossAllProducts'),
      highlight: true,
    },
    {
      label: t('banking.loansProductWise.kpi.topProductShare'),
      value: `${mix[0]!.sharePct.toFixed(2)}%`,
      sublabel: mix[0]!.label,
    },
    {
      label: t('banking.loansProductWise.kpi.top2ProductsShare'),
      value: `${top2Share.toFixed(2)}%`,
      sublabel: `${mix[0]!.label} + ${mix[1]!.label}`,
    },
    {
      label: t('banking.loansProductWise.kpi.numberOfProducts'),
      value: String(mix.length),
      sublabel: t('banking.loansProductWise.kpi.acrossAllCategories'),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.loansProductWise.chartTitle')}
        subtitle={t('banking.loansProductWise.chartSubtitle', {
          branch: branchLabel(branchId),
          fy,
        })}
      >
        <PieBreakdownCard
          segments={segments}
          valueFormatter={(v) => formatCr(v)}
          totalLabel={t('banking.total')}
          legendPosition="right"
          height={560}
          categoryColumnLabel={t('banking.loansProductWise.categoryColumn')}
          valueColumnLabel={t('banking.amountCrColumn')}
        />
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          t('banking.loansProductWise.summary.line1', {
            product1: mix[0]!.label,
            share1: mix[0]!.sharePct.toFixed(2),
            product2: mix[1]!.label,
            share2: mix[1]!.sharePct.toFixed(2),
            top2Share: top2Share.toFixed(2),
          }),
          t('banking.loansProductWise.summary.line2', {
            product1: mix[mix.length - 3]!.label,
            product2: mix[mix.length - 2]!.label,
            product3: mix[mix.length - 1]!.label,
          }),
        ]}
      />
    </div>
  )
}

export default BankingLoansProductWisePage
